/**
 * runtimeStore.js — Observable singleton state for the active session.
 *
 * Tracks what Kevia "knows" about the user's current operational context:
 *   - what module they're in
 *   - what entity is active
 *   - what they did last
 *   - a rolling window of recent events for quick access
 *
 * Design constraints:
 *  - Zero React dependencies — pure JS, safe to import anywhere
 *  - No external libraries
 *  - Observers receive a snapshot (not a reference) to prevent mutation bugs
 *  - emit() is called by pushRuntimeEvent() in index.js — never call directly
 *    from components; go through pushRuntimeEvent() so timeline is always in sync
 *
 * Future: connect to Supabase realtime channel so cross-tab state sync
 * is possible without re-architecting the store interface.
 */

import { RT_EVENTS } from "./runtimeEvents";

/* ─── State shape ─────────────────────────────────────────────────────── */

const _state = {
  currentModule: null,   // string | null
  currentRoute:  null,   // string | null
  activeEntity:  null,   // { type, id, name } | null
  lastAction:    null,   // { type, ts, label } | null
  recentEvents:  [],     // RuntimeEvent[] — last 10, newest last
};

/* ─── Observers ───────────────────────────────────────────────────────── */

const _listeners = new Set();

function notify() {
  const snapshot = getState();
  for (const fn of _listeners) {
    try { fn(snapshot); } catch { /* subscriber errors must not break the store */ }
  }
}

/* ─── Public API ──────────────────────────────────────────────────────── */

/** Returns a shallow snapshot of current state. */
export function getState() {
  return {
    ..._state,
    activeEntity: _state.activeEntity ? { ..._state.activeEntity } : null,
    lastAction:   _state.lastAction   ? { ..._state.lastAction }   : null,
    recentEvents: _state.recentEvents.slice(),
  };
}

/**
 * Merges a partial state object and notifies subscribers.
 * Use for programmatic overrides (e.g. setting currentIntent from selectors).
 */
export function setState(partial) {
  Object.assign(_state, partial);
  notify();
}

/**
 * Subscribes to state changes.
 * @param {(snapshot: object) => void} fn
 * @returns {() => void} unsubscribe function
 */
export function subscribe(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

/**
 * Processes a runtime event into derived state.
 * Called exclusively by pushRuntimeEvent() in index.js.
 *
 * @param {RuntimeEvent} event
 */
export function emit(event) {
  // Rolling window of recent events (quick access without scanning timeline)
  _state.recentEvents = [..._state.recentEvents, event].slice(-10);

  // Derive current module / route
  if (event.module) _state.currentModule = event.module;
  if (event.route)  _state.currentRoute  = event.route;

  // Track the last known entity
  if (event.type === RT_EVENTS.OPEN_ENTITY && event.entityId) {
    _state.activeEntity = {
      type: event.entityType,
      id:   event.entityId,
      name: event.entityName,
    };
  }

  // Track the last user action for context injection
  _state.lastAction = {
    type:  event.type,
    ts:    event.ts,
    label: event.metadata?.label ?? event.type,
  };

  notify();
}
